// src/styles/glow.js

import { css, keyframes } from 'styled-components';

/* The travelling border highlight, lifted out of the chat panel so the two
   places that use it cannot drift apart.

   Painted as the border itself rather than as a ring inside it: a transparent
   1px border plus a conic gradient clipped to the border box by a mask that
   cuts the padding box out, so the gradient shows only in the border band and
   the interior stays genuinely transparent. Only the gradient's angle is
   animated, so the highlight travels without the shape rotating.

   --glow-angle is registered with @property in GlobalStyle. Without that
   registration a custom property jumps between keyframes instead of
   interpolating, and the highlight would blink rather than travel. */

export const spinGlow = keyframes`
  to {
    --glow-angle: 360deg;
  }
`;

export const travellingRing = css`
  position: relative;

  /* The ring IS the border, so the element's own border has to get out of the
     way or it shows as a grey outline with the highlight travelling inside it.
     Cleared only in the resting state: :hover carries higher specificity, so
     the button mixin's accent border still takes over on hover, which is
     exactly when the ring fades out. */
  border-color: transparent;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    border-radius: inherit;
    padding: 1px;
    background: conic-gradient(
      from var(--glow-angle),
      transparent 0deg,
      transparent 160deg,
      var(--accent-tint) 230deg,
      var(--accent) 300deg,
      var(--accent-tint) 340deg,
      transparent 360deg
    );
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
    pointer-events: none;
    opacity: 1;
    transition: opacity 0.2s var(--easing);
    animation: ${spinGlow} 5s linear infinite;
  }

  /* Hover hands the border to a solid accent, and the two cannot both be
     right: the ring would keep travelling a pixel inside the new border and
     read as a highlight rattling around inside the outline. The ring belongs
     to the resting state. */
  &:hover::before,
  &:focus-visible::before {
    opacity: 0;
  }

  /* No ring to be the border, so the real one comes back. */
  @media (prefers-reduced-motion: reduce) {
    border-color: var(--border-strong);

    &::before {
      display: none;
    }
  }
`;
